/**
 * Minimal API client with JWT auth.
 * Token stored in localStorage (MVP — refactor to httpOnly cookies later if needed).
 */

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost'
const TOKEN_KEY = 'corsalia.jwt'

/**
 * Thrown by {@link apiFetch} on any non-2xx response. Real Error subclass so:
 *   - `instanceof ApiError` narrows in catch / mutation onError handlers,
 *   - `err.message` is typed by virtue of extending Error (no cast needed
 *     at the dozens of toast call sites),
 *   - `err.status` / `err.errors` stay accessible after the narrowing check.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly errors?: Record<string, string[]>,
    /** Machine-readable domain code (the kernel exception listener's `reason`), e.g. `insufficient_wood`. */
    public readonly reason?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const tokenStorage = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string): void => localStorage.setItem(TOKEN_KEY, token),
  clear: (): void => localStorage.removeItem(TOKEN_KEY),
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = tokenStorage.get()
  const headers = new Headers(options.headers)

  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json')
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json')
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(`${API_URL}${path}`, { ...options, headers })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const data = text ? (JSON.parse(text) as unknown) : null

  if (!response.ok) {
    // Two backend error shapes: some controllers send {error: code, message: human},
    // the kernel exception listener sends {error: human, reason: code}. Prefer the
    // human `message`, fall back to `error` so domain-exception messages surface.
    const errData = data as { message?: string; error?: string; errors?: Record<string, string[]>; reason?: string } | null
    throw new ApiError(
      response.status,
      errData?.message ?? errData?.error ?? `Request failed with status ${response.status}`,
      errData?.errors,
      errData?.reason,
    )
  }

  return data as T
}
